// ─────────────────────────────────────────────────────────────────────────────
//  Refer to Diagnostic — send a patient's labs/scans to the partner diagnostic
//
//  The doctor picks from the DIAGNOSTIC's own test list (fetched through our
//  backend), so nothing needs mapping on their side. No payment is taken
//  here: the diagnostic's Records desk registers the patient, generates the
//  CCC, and the tests go to their lab from there.
//
//  The button renders nothing when the link is not configured on the server
//  (DIAGNOSTIC_* in backend/.env), so installations without a partner are
//  unaffected.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, MenuItem, Stack, TextField,
  Tooltip, Typography, Alert, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Send as SendIcon, Close as CloseIcon, Refresh as RefreshIcon,
  Biotech as BiotechIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  diagnosticReferralConfig, diagnosticReferralCatalog, diagnosticReferralCreate,
  diagnosticReferralList, diagnosticReferralRefresh, diagnosticReferralPrefill,
} from '../BaseURL';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const STATUS_LOOK = {
  queued:      { label: 'Waiting to send', color: 'default' },
  received:    { label: 'At diagnostic — awaiting registration', color: 'info' },
  registered:  { label: 'Registered — in lab queue', color: 'primary' },
  in_progress: { label: 'In progress', color: 'warning' },
  completed:   { label: 'Results ready', color: 'success' },
  rejected:    { label: 'Rejected', color: 'error' },
  failed:      { label: 'Not accepted', color: 'error' },
};

export default function DiagnosticReferralButton({ patient, currentUser, defaultDiagnosis = '', visitDate }) {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState('');
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  const [tests, setTests] = useState([]);
  const [diagnosis, setDiagnosis] = useState('');
  const [note, setNote] = useState('');
  const [urgency, setUrgency] = useState('routine');
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Mandatory referral details. CCC and prescriber come from this visit's
  // claim; facility code/name are this facility's and are only shown.
  const [refInfo, setRefInfo] = useState({ ccc: '', prescriber: '', prescriberCode: '', date: today() });
  // code is null until the server answers, '' when it answered "not set".
  const [facility, setFacility] = useState({ code: null, name: '', needsCcc: true, scheme: '' });

  useEffect(() => {
    axios.get(diagnosticReferralConfig)
      .then(r => setConfig(r.data))
      .catch(() => setConfig({ enabled: false }));
  }, []);

  const loadHistory = useCallback(async (refresh = false) => {
    if (!patient?.id) return;
    setRefreshing(refresh);
    try {
      const r = refresh
        ? await axios.post(diagnosticReferralRefresh, { patientId: patient.id })
        : await axios.get(diagnosticReferralList, { params: { patientId: patient.id } });
      setHistory(r.data?.results || []);
    } catch (_) { /* the list is a courtesy; the form still works */ }
    finally { setRefreshing(false); }
  }, [patient?.id]);

  const openDialog = async () => {
    setOpen(true);
    setTests([]);
    setNote('');
    setUrgency('routine');
    setDiagnosis(defaultDiagnosis || '');
    loadHistory(true);
    setRefInfo({ ccc: '', prescriber: currentUser || '', prescriberCode: '', date: today() });
    setFacility({ code: null, name: '', needsCcc: true, scheme: '' });
    axios.get(diagnosticReferralPrefill, { params: { patientId: patient.id, visitDate } })
      .then(r => {
        const d = r.data || {};
        setFacility({ code: d.facilityCode || '', name: d.facilityName || '', needsCcc: d.needsCcc !== false, scheme: d.scheme || '' });
        setRefInfo(prev => ({
          ...prev,
          ccc: d.ccc || '',
          prescriber: d.prescriber || prev.prescriber,
          prescriberCode: d.prescriberCode || '',
        }));
      })
      .catch(() => { /* fields stay editable; the server re-checks on send */ });
    if (!catalog.length) {
      setLoadingCatalog(true);
      setCatalogError('');
      try {
        const r = await axios.get(diagnosticReferralCatalog);
        setCatalog(r.data?.tests || []);
      } catch (err) {
        setCatalogError(err.response?.data?.message || 'Could not load the diagnostic centre\'s test list');
      } finally {
        setLoadingCatalog(false);
      }
    }
  };

  const send = async () => {
    if (!tests.length) return;
    setSending(true);
    try {
      const r = await axios.post(diagnosticReferralCreate, {
        patientId: patient.id, tests, diagnosis, clinicalNote: note, urgency, currentUser,
        referral: refInfo,
      });
      await Swal.fire({
        icon: r.data.status === 'received' ? 'success' : 'info',
        title: r.data.status === 'received' ? 'Referral sent' : 'Referral saved',
        text: r.data.message,
        timer: r.data.status === 'received' ? 2500 : undefined,
      });
      setTests([]);
      setNote('');
      loadHistory();
    } catch (err) {
      Swal.fire('Not sent', err.response?.data?.message || 'Could not create the referral', 'error');
    } finally {
      setSending(false);
    }
  };

  const setRef = (k) => (e) => setRefInfo(prev => ({ ...prev, [k]: e.target.value }));
  const missingRef = [
    !facility.code && 'facility NHIS code',
    facility.needsCcc && !refInfo.ccc.trim() && 'referring CCC',
    !refInfo.prescriber.trim() && 'prescriber',
    !refInfo.date && 'referral date',
  ].filter(Boolean);

  if (!config?.enabled || !patient?.id) return null;

  return (
    <>
      <Tooltip title={`Send labs/scans to ${config.name} — no payment here`}>
        <Button variant="contained" color="secondary" startIcon={<BiotechIcon />} onClick={openDialog}>
          Refer to Diagnostic
        </Button>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" fullScreen={isCompact}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }}>Refer to {config.name}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {patient.name} • {patient.id}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            No payment is taken here. {config.name} registers the patient, generates the CCC and runs the tests.
          </Alert>

          {catalogError && <Alert severity="error" sx={{ mb: 2 }}>{catalogError}</Alert>}

          <Autocomplete
            multiple
            options={catalog}
            loading={loadingCatalog}
            value={tests}
            onChange={(_e, v) => setTests(v)}
            groupBy={(o) => (o.type === 'Scan' ? 'Scans' : 'Labs')}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.name === b.name && a.type === b.type}
            filterSelectedOptions
            renderTags={(value, getTagProps) => value.map((o, i) => (
              <Chip {...getTagProps({ index: i })} key={`${o.type}-${o.name}`} size="small"
                label={o.name} color={o.type === 'Scan' ? 'info' : 'default'} />
            ))}
            renderInput={(params) => (
              <TextField {...params} label="Tests to refer *" placeholder="Type to search labs and scans"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (<>{loadingCatalog ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>),
                }} />
            )}
            sx={{ mb: 2 }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField label="Diagnosis" value={diagnosis} onChange={e => setDiagnosis(e.target.value)} fullWidth />
            <TextField select label="Urgency" value={urgency} onChange={e => setUrgency(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="routine">Routine</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
              <MenuItem value="stat">STAT</MenuItem>
            </TextField>
          </Stack>

          <TextField label="Clinical note for the diagnostic centre" value={note}
            onChange={e => setNote(e.target.value)} fullWidth multiline minRows={2} />

          {/* ── Referral details — required on the diagnostic's NHIA claim ── */}
          <Typography sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>Referral details (required)</Typography>
          {facility.code === '' && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              This facility has no NHIS facility code. An administrator must enter it in Admin Panel → Diagnostic Referral Link before referring.
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField label="Referring facility" value={facility.name || '—'} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="Facility NHIS code" value={facility.code === null ? 'Loading…' : (facility.code || 'Not set')}
              fullWidth InputProps={{ readOnly: true }} error={facility.code === ''} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField label={facility.needsCcc ? 'Referring CCC *' : 'Referring CCC (not needed — cash patient)'}
              value={refInfo.ccc} onChange={setRef('ccc')} fullWidth
              error={facility.needsCcc && !refInfo.ccc.trim()}
              helperText={facility.needsCcc && !refInfo.ccc.trim() ? 'No CCC on this visit — generate or enter it' : 'From this visit'} />
            <TextField label="Referral date *" type="date" value={refInfo.date} onChange={setRef('date')} fullWidth
              InputLabelProps={{ shrink: true }} inputProps={{ max: today() }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Prescriber *" value={refInfo.prescriber} onChange={setRef('prescriber')} fullWidth
              error={!refInfo.prescriber.trim()} />
            <TextField label="Prescriber ID / code" value={refInfo.prescriberCode} onChange={setRef('prescriberCode')} fullWidth />
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>Referrals for this patient</Typography>
            <Tooltip title="Get the latest status from the diagnostic centre">
              <span>
                <IconButton size="small" onClick={() => loadHistory(true)} disabled={refreshing}>
                  {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {!history.length && <Typography variant="body2" color="text.secondary">None yet.</Typography>}
          <Stack spacing={1}>
            {history.map(h => {
              const look = STATUS_LOOK[h.status] || { label: h.status, color: 'default' };
              return (
                <Box key={h.id} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{h.tests_summary}</Typography>
                    <Chip size="small" color={look.color} label={look.label} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(h.created_at).toLocaleString()} • {h.referring_doctor}
                    {h.remote_note ? ` • ${h.remote_note}` : ''}
                    {h.status === 'queued' && h.last_error ? ` • ${h.last_error}` : ''}
                    {h.status === 'failed' && h.last_error ? ` • ${h.last_error}` : ''}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={!tests.length || sending || missingRef.length > 0}
            title={missingRef.length ? `Missing: ${missingRef.join(', ')}` : ''}
            onClick={send}>
            Send referral
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
